import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";

import logo from "../../Image/logo.png";
import onlyLogo from "../../Image/Only_Logo .png";

import {
  MdOutlineLogout,
  MdMonitor,
  MdMenu,
  MdClose,
  MdDashboard,
  MdKeyboardArrowRight,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";

import { GrUserManager } from "react-icons/gr";
import { HiUserAdd } from "react-icons/hi";
import { RiLuggageDepositFill } from "react-icons/ri";

export default function Layout() {
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const contentCollapsed = sidebarCollapsed && !sidebarOpen;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedUsername = localStorage.getItem("username");
    const savedRole = localStorage.getItem("userrole");

    if (!savedUsername || !savedRole) {
      navigate("/");
      return;
    }

    setUsername(savedUsername);
    setRole(savedRole);
    setLoading(false);
  }, [navigate]);

  // Close mobile sidebar whenever route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-sky-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  const menuItem = (path, icon, label) => {
    const isActive =
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`);

    return (
      <Link
        to={path}
        title={contentCollapsed ? label : undefined}
        className={`
          group relative flex items-center gap-3
          w-full min-h-[44px]
          py-2.5
          ${contentCollapsed ? "justify-center px-0" : "px-3"}
          rounded-xl
          text-sm font-medium
          transition-all duration-200
          ${
            isActive
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-300 hover:bg-white/10 hover:text-white"
          }
        `}
      >
        {/* Active indicator */}
        {isActive && (
          <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-sky-500" />
        )}

        {/* Icon */}
        <span
          className={`
            flex items-center justify-center
            w-8 h-8
            rounded-lg
            flex-shrink-0
            transition-all duration-200
            ${
              isActive
                ? "bg-sky-50 text-sky-600"
                : "text-slate-400 group-hover:text-sky-300"
            }
          `}
        >
          {icon}
        </span>

        {/* Label */}
        {!contentCollapsed && <span className="flex-1 truncate">{label}</span>}

        {/* Arrow */}
        {!contentCollapsed && (
          <MdKeyboardArrowRight
            size={18}
            className={`
              transition-all duration-200
              ${
                isActive
                  ? "text-slate-400"
                  : "text-slate-600 opacity-0 group-hover:opacity-100"
              }
            `}
          />
        )}
      </Link>
    );
  };

  const getRoleLabel = () => {
    if (role === "client") return "Client";
    if (role === "support") return "Support";
    return role;
  };

  const getInitials = () => {
    if (!username) return "U";

    return username
      .trim()
      .split(" ")
      .map((name) => name[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50">

      {/* =====================================================
          HEADER
      ===================================================== */}
      <header
        className="
          fixed top-0 left-0 right-0
          h-16
          bg-white
          border-b border-slate-200
          z-50
        "
      >
        <div className="h-full flex items-center">

          {/* Desktop logo area */}
          <div
            className={`
              hidden lg:flex
              items-center
              justify-center
                  transition-all duration-300
                  ${sidebarCollapsed ? "w-[72px]" : "w-[250px]"}
              h-full
              border-r border-slate-200
              px-5
            `}
          >
            <img
              src={sidebarCollapsed ? onlyLogo : logo}
              alt="Logo"
              className={sidebarCollapsed ? "w-10 h-10 object-contain" : "w-40 h-40 object-contain"}
            />
          </div>

          {/* Mobile header */}
          <div className="lg:hidden flex items-center px-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="
                flex items-center justify-center
                w-10 h-10
                rounded-lg
                text-slate-600
                hover:bg-slate-100
                transition
              "
              aria-label="Toggle navigation"
            >
              {sidebarOpen ? (
                <MdClose size={24} />
              ) : (
                <MdMenu size={24} />
              )}
            </button>

            <img
              src={logo}
              alt="Logo"
              className="w-24 h-24 object-contain ml-2"
            />
          </div>

          {/* Header right */}
          <div className="flex-1 flex items-center justify-end px-3 sm:px-5 lg:px-6">

            {/* User profile */}
            <div className="flex items-center gap-3">

              {/* Avatar */}
              <div
                className="
                  flex items-center justify-center
                  w-9 h-9
                  rounded-full
                  bg-sky-100
                  text-sky-700
                  font-bold
                  text-xs
                "
              >
                {getInitials()}
              </div>

              {/* User details */}
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-slate-800 leading-5">
                  {username}
                </p>

                <p className="text-xs text-slate-500 capitalize">
                  {getRoleLabel()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}
      {sidebarOpen && (
        <div
          className="
            fixed inset-0
            bg-slate-950/50
            backdrop-blur-[2px]
            z-30
            lg:hidden
          "
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* =====================================================
          SIDEBAR
      ===================================================== */}
      <aside
        className={`
          fixed
          left-0
          top-16
          z-40
          h-[calc(100vh-4rem)]
          transition-[width,transform]
          duration-300
          bg-sky-800
          border-r border-slate-800
          shadow-xl

          flex flex-col

          transform
          w-[250px]
          ${sidebarCollapsed ? "lg:w-[72px]" : "lg:w-[250px]"}

          ${
            sidebarOpen
              ? "translate-x-0"
              : "-translate-x-full"
          }

          lg:translate-x-0
        `}
      >

        {/* =================================================
            SIDEBAR BRAND
        ================================================= */}
        <div className={`px-4 pt-5 pb-4 ${contentCollapsed ? "px-3" : ""}`}>

          <div className={`flex items-center ${contentCollapsed ? "justify-center" : "justify-between px-3"}`}>

            {/* <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Management Portal
            </p> */}

            {!contentCollapsed && (
              <h1 className="mt-1 text-lg font-bold text-white">
                Issue Tracker
              </h1>
            )}

            <button
              type="button"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex h-8 w-8 items-center justify-center rounded-lg text-slate-300 transition hover:bg-white/10 hover:text-white"
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <MdChevronRight size={22} /> : <MdChevronLeft size={22} />}
            </button>

          </div>
        </div>

        {/* =================================================
            NAVIGATION
        ================================================= */}
        <div className={`flex-1 overflow-y-auto pb-24 ${contentCollapsed ? "px-2" : "px-3"}`}>

          {/* Navigation heading */}
          {/* <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
            Navigation
          </p> */}

          <nav className="space-y-1.5">

            {/* CLIENT */}
            {role === "client" && (
              <>
                {menuItem(
                  "/clientdashboard",
                  <MdDashboard size={19} />,
                  "Dashboard"
                )}

                {/* {menuItem(
                  "/adminuser",
                  <HiUserAdd size={19} />,
                  "Admin Users"
                )}

                {menuItem(
                  "/add_dep",
                  <RiLuggageDepositFill size={19} />,
                  "Departments"
                )} */}
              </>
            )}

            {/* SUPPORT */}
            {role === "support" && (
              <>
                {menuItem(
                  "/supportdashboard",
                  <MdMonitor size={19} />,
                  "Support Dashboard"
                )}
              </>
            )}

            {/* ADMIN */}
            {role === "admin" && (
              <>
                {menuItem(
                  "/supportdashboard",
                  <MdMonitor size={19} />,
                  "All Tickets"
                )}
                {menuItem(
                  "/adminuser",
                  <HiUserAdd size={19} />,
                  "Users"
                )}
                {menuItem(
                  "/add_dep",
                  <RiLuggageDepositFill size={19} />,
                  "Departments"
                )}
                {menuItem(
                  "/add_branch",
                  <RiLuggageDepositFill size={19} />,
                  "Branches"
                )}
              </>
            )}
          </nav>
        </div>

        {/* =================================================
            USER / LOGOUT
        ================================================= */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-sKY-900  border-slate-800">

          {/* User mini profile */}
          <div className={`flex items-center rounded-xl bg-slate-800/60 py-2.5 mb-2 ${contentCollapsed ? "justify-center px-0" : "gap-3 px-3"}`}>

            <div
              className="
                flex items-center justify-center
                w-9 h-9
                rounded-full
                bg-sky-500/15
                text-sky-400
                text-xs
                font-bold
              "
            >
              {getInitials()}
            </div>

            {!contentCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-200 truncate">
                  {username}
                </p>

                <p className="text-xs text-slate-500 capitalize">
                  {getRoleLabel()}
                </p>
              </div>
            )}

          </div>

          {/* Logout */}
          <Link
            to="/"
            onClick={() => {
              localStorage.clear();
              setSidebarOpen(false);
            }}
            className="
              group
              flex items-center justify-center gap-2
              w-full
              min-h-[42px]
              px-3
              rounded-xl
              bg-red-500
              border border-slate-700
              text-slate-300
              text-sm
              font-medium
              hover:bg-red-600
              hover:border-red-500/30
              
              transition-all duration-200
            "
          >
            <MdOutlineLogout
              size={19}
              className="group-hover:-translate-x-0.5 transition-transform"
            />

            {!contentCollapsed && <span>Logout</span>}
          </Link>
        </div>
      </aside>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}
      <main
        className={`
          pt-16
          min-h-screen

          transition-[margin] duration-300
          ${sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-[250px]"}

          px-3
          sm:px-5
          md:px-6
          lg:px-8
          xl:px-10

          py-5
          sm:py-6
          lg:py-7
        `}
      >
        <div className="w-full max-w-[1800px] mx-auto">
          <Outlet />
        </div>
      </main>

    </div>
  );
}